/**
 * Which vendor invoice the address bar names (lane 5, V5), and the address an open invoice has.
 *
 * Two shapes name one invoice: the path `/vendor-invoices/<id>`, which is what a person copies and
 * what staff expect to open, and the query `/vendor-invoices?selected=<id>`, which the copilot's links
 * (`chatAssistant.service`, `findVendorInvoices.tool`) have always carried and which the page used to
 * ignore, so those links opened the list and no invoice. Pure, and read from `useLocation()`'s own
 * fields so a test double with a bare `{ pathname }` works.
 */

const BASE = "/vendor-invoices";

/** The id the location names, or null: not on this screen, a deeper path, a blank selection, a bad escape. */
export function invoiceIdFromLocation(
  location: { pathname?: string; search?: string } | null | undefined
): string | null {
  const path = String(location?.pathname ?? "");
  const named = /^\/vendor-invoices\/([^/?#]+)\/?$/.exec(path);
  if (named) {
    try {
      return decodeURIComponent(named[1]);
    } catch {
      // A malformed escape ("%E0%A4%A") names nothing, so it is no link, not a guess at one.
      return null;
    }
  }
  if (path !== BASE && path !== `${BASE}/`) return null;
  const selected = new URLSearchParams(String(location?.search ?? "")).get("selected");
  return selected && selected.trim() ? selected.trim() : null;
}

/** The address the screen shows for an open invoice (`/vendor-invoices/<id>`), or for none, the bare list. */
export function invoicePath(id: string | null | undefined): string {
  return id ? `${BASE}/${encodeURIComponent(id)}` : BASE;
}
